import { Editor } from '@tiptap/core';
import { EditorNodeInfo } from '../../../types';
import { getBlockNodeFromPosition } from '../../../utils/position-utils';

const endOfSentenceDelimiter = '. ';

interface TextBoundaries {
  contentBeforePosition: string;
  contentAfterPosition: string;
  entireContent: string;
  nodeStartPosition: number;
  nodeEndPosition: number;
  relativePosition: number;
}

const calculateTextBoundaries = (
  nodeInfo: EditorNodeInfo,
  position: number,
): TextBoundaries => {
  const textContent = nodeInfo.node.textContent;
  const relativePosition = position - nodeInfo.start;

  return {
    contentBeforePosition: textContent.substring(0, relativePosition),
    contentAfterPosition: textContent.substring(relativePosition),
    entireContent: textContent,
    nodeStartPosition: nodeInfo.start,
    nodeEndPosition: nodeInfo.end,
    relativePosition,
  };
};

const modifyPositionToMoveAroundSentence = (
  nodeInfo: EditorNodeInfo,
  originalPosition: number,
  toStart: boolean,
) => {
  const {
    contentBeforePosition,
    contentAfterPosition,
    nodeStartPosition,
    nodeEndPosition,
  } = calculateTextBoundaries(nodeInfo, originalPosition + (toStart ? 1 : -1));

  const indexOfDelimiter = toStart
    ? contentBeforePosition.lastIndexOf(endOfSentenceDelimiter)
    : contentAfterPosition.indexOf(endOfSentenceDelimiter);

  if (indexOfDelimiter === -1) {
    return toStart ? nodeStartPosition : nodeEndPosition;
  }

  if (toStart) {
    const sentenceStartPosition =
      indexOfDelimiter + endOfSentenceDelimiter.length + nodeStartPosition;

    return sentenceStartPosition <= originalPosition ||
      originalPosition + 1 === sentenceStartPosition
      ? sentenceStartPosition
      : nodeStartPosition;
  }

  const sentenceEndPosition =
    indexOfDelimiter + 1 + nodeStartPosition + contentBeforePosition.length;

  return sentenceEndPosition >= originalPosition
    ? sentenceEndPosition
    : nodeEndPosition;
};

export const expandSelectionPositionToIncludeEntireSentence = (
  editor: Editor,
  from: number,
  to: number,
) => {
  const startNodeInfo = getBlockNodeFromPosition(editor, from);
  const endNodeInfo = getBlockNodeFromPosition(editor, to);

  return {
    from:
      startNodeInfo == null
        ? from
        : modifyPositionToMoveAroundSentence(startNodeInfo, from, true),
    to:
      endNodeInfo == null
        ? to
        : modifyPositionToMoveAroundSentence(endNodeInfo, to, false),
  };
};
